import { useEffect } from 'react';

import { Button } from '@bambi/component-library/src/components/Button';
import { ContextFormFieldSelect } from '@bambi/component-library/src/components/forms/context/ContextFormFieldSelect';
import { ContextFormFieldTextArea } from '@bambi/component-library/src/components/forms/context/ContextFormFieldTextArea';
import { FormProvider, useForm } from 'react-hook-form';

import { useAppDispatch } from 'src/app/store/hooks';
import {
  DriverRead,
  PreferenceTypeEnum,
  PreferredDriverRead,
  usePassengersPreferredDriversCreateMutation,
  usePassengersPreferredDriversPartialUpdateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { Modal } from 'src/common/primitives/Modal/Modal';
import { show } from 'src/common/primitives/Toast/toast.slice';
import { formatFullName } from 'src/common/util/formatFullName';
import formatServerError from 'src/common/util/serverErrorFormatter';

const preferenceTypeOptions = [
  { label: 'Avoid', value: 'avoid' },
  { label: 'Demand', value: 'demand' },
  { label: 'Prefer', value: 'prefer' },
  { label: 'Prohibit', value: 'prohibit' },
];

export type PreferredDriverFormValues = {
  driver?: string;
  preference_type?: PreferenceTypeEnum;
  notes?: string;
};

interface PreferredDriverFormModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onClose?: () => void;
  passengerId: string;
  preferredDriver?: PreferredDriverRead;
  drivers: DriverRead[];
}

export function PreferredDriverFormModal({
  open,
  setOpen,
  onClose = () => {},
  passengerId,
  preferredDriver,
  drivers,
}: PreferredDriverFormModalProps) {
  const dispatch = useAppDispatch();
  const isEditing = !!preferredDriver;

  const driverOptions = drivers.map((driver) => ({
    label: formatFullName(driver),
    value: driver.id,
  }));

  const methods = useForm<PreferredDriverFormValues>({
    defaultValues: {
      driver: preferredDriver?.driver,
      preference_type: preferredDriver?.preference_type,
      notes: preferredDriver?.notes,
    },
  });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (open) {
      reset({
        driver: preferredDriver?.driver,
        preference_type: preferredDriver?.preference_type,
        notes: preferredDriver?.notes,
      });
    }
  }, [open, preferredDriver, reset]);

  const [createPreference] = usePassengersPreferredDriversCreateMutation();
  const [updatePreference] =
    usePassengersPreferredDriversPartialUpdateMutation();

  const handleClose = () => {
    reset();
    setOpen(false);
    onClose();
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (!data.driver || !data.preference_type) {
        // this should be caught by the form validation
        // adding return to make typescript happy
        return;
      }

      if (isEditing) {
        await updatePreference({
          passengerId,
          id: preferredDriver.id,
          patchedPreferredDriver: data,
        }).unwrap();
        dispatch(show({ title: 'Preference updated', type: 'success' }));
      } else {
        await createPreference({
          passengerId,
          preferredDriver: {
            driver: data.driver,
            preference_type: data.preference_type,
            notes: data.notes,
          },
        }).unwrap();
        dispatch(show({ title: 'Preference added', type: 'success' }));
      }
      handleClose();
    } catch (e) {
      dispatch(
        show({
          title: isEditing
            ? 'Failed to update preference'
            : 'Failed to add preference',
          description: formatServerError(e),
          type: 'error',
        })
      );
    }
  });

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      onClose={() => reset()}
      contentClassnames="w-[400px]"
      title={isEditing ? 'Edit Driver Preference' : 'Add Driver Preference'}
    >
      <div className="flex flex-col gap-4">
        <h4 className="text-lg font-medium text-gray-900">
          {isEditing ? 'Edit Driver Preference' : 'Add Driver Preference'}
        </h4>
        <FormProvider {...methods}>
          <ContextFormFieldSelect
            fieldPath="driver"
            label="Driver"
            options={driverOptions}
            placeholder="Select a driver"
            unwrapValue
            rules={{ required: 'Driver is required' }}
          />
          <ContextFormFieldSelect
            fieldPath="preference_type"
            label="Preference Type"
            options={preferenceTypeOptions}
            placeholder="Select preference type"
            unwrapValue
            rules={{ required: 'Preference type is required' }}
          />
          <ContextFormFieldTextArea
            fieldPath="notes"
            label="Notes (optional)"
          />
        </FormProvider>
      </div>
      <div className="mt-4 flex flex-row justify-between gap-2">
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onSubmit}>
          {isEditing ? 'Save' : 'Add'}
        </Button>
      </div>
    </Modal>
  );
}
